import { useEffect, useRef } from 'react';
import type { ProfileColorKey } from '@maios/shared';
import Avatar from '../common/Avatar';

interface MemberItem {
  userId: string;
  name: string;
  avatarUrl?: string | null;
  // CHG-131: 공용 Avatar 사용 — displayColor 있으면 활용
  displayColor?: ProfileColorKey | null;
}

interface SpecialItem {
  id: string;
  name: string;
  description: string;
}

interface Props {
  members: MemberItem[];
  specials: SpecialItem[];
  selectedIndex: number;
  onSelect: (userId: string, name: string) => void;
}

export default function MentionDropdown({ members, specials, selectedIndex, onSelect }: Props) {
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${selectedIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (members.length === 0 && specials.length === 0) {
    return (
      <div ref={listRef} className="absolute bottom-full left-0 right-0 mb-1 bg-white shadow-lg rounded-lg border py-1 z-30">
        <div className="px-3 py-2 text-sm text-gray-400">멤버를 찾을 수 없습니다</div>
      </div>
    );
  }

  return (
    <div ref={listRef} className="absolute bottom-full left-0 right-0 mb-1 bg-white shadow-lg rounded-lg border max-h-52 overflow-y-auto py-1 z-30">
      {members.map((m, i) => (
        <button key={m.userId} data-idx={i}
          className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors
            ${selectedIndex === i ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
          onMouseDown={(e) => { e.preventDefault(); onSelect(m.userId, m.name); }}>
          <Avatar
            userId={m.userId}
            name={m.name}
            avatarUrl={m.avatarUrl}
            displayColor={m.displayColor ?? undefined}
            size="xs"
          />
          <span className="font-medium text-gray-800">{m.name}</span>
        </button>
      ))}
      {specials.length > 0 && members.length > 0 && <hr className="my-1 border-gray-100" />}
      {specials.map((s, i) => (
        <button key={s.id} data-idx={members.length + i}
          className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors
            ${selectedIndex === members.length + i ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
          onMouseDown={(e) => { e.preventDefault(); onSelect(s.id, s.name); }}>
          <span className="size-6 flex items-center justify-center text-base shrink-0">
            {s.id === '@all' ? '🌐' : '🟢'}
          </span>
          <span className="font-medium text-gray-800">@{s.name}</span>
          <span className="text-gray-400 text-sm">— {s.description}</span>
        </button>
      ))}
    </div>
  );
}
